'use strict';

import axios from 'axios';

import Api from '@/api/vacancy.js';
import searchbot from './vacancy/searchbot.js';
import voicebot from './vacancy/voicebot.js';
import videointerview from './vacancy/videointerview.js';
import invitation from './vacancy/invitation.js';
import sms from './vacancy/sms.js';
import url from './vacancy/url.js';
import test from './vacancy/test.js';
// import publicRegistration from './vacancy/publicRegistration.js'
import preferences from './vacancy/preferences.js';
import doNotAllowToSave from './vacancy/doNotAllowToSave.js';
import application from './vacancy/application.js';

import { parseResponse, handleError, setFullName } from '@/_helpers/utils.js';

const getInitialModalAlertParams = () => {
  return {
    show: false,
    options: null
  };
};

const state = {
  loadingVacancy: false,
  loading: false,
  error: null,
  vacancy: null,
  route: null,
  new_vacancy_id: null,
  permissions: [],
  permissionsToDelete: [],
  competencies: [],
  competenciesToDelete: [],
  newPermission: null,
  isDeleted: false,
  availableCompetencies: {},
  validCompetences: {},
  lastSaveTimestamp: null,
  lastUpdateTimestamp: null,
  lastSaveTimestampCompetencies: null,
  lastUpdateTimestampCompetencies: null,
  emailTemplatesDefault: [],
  emailTemplatesAll: [],
  surveyScreeningTemplate: null,
  testDropoutTemplate: null,
  notificationTemplate: null,
  change: false,
  landing_url: null,
  local_landing_url_path: null,
  modals: {
    alert: getInitialModalAlertParams()
  }
};

const actions = {
  fetchVacancy({ commit, dispatch }, { id }) {
    commit('fetchVacancy');

    return Api.fetchOne(id)
      .then(data => {
        // determine active process
        // by vacancy first
        let activeProcessName = data.meta && data.meta.active_process ? data.meta.active_process : null;

        // if no vacancy special process, get process id from vacancy compnay
        // this is quite narrow place id o not like it:
        // this vacancy.company special process identification only affected in vacancy edition page (!)
        // must not be applied to responses page (!)
        if (!activeProcessName) {
          activeProcessName =
            data.company && data.company.meta && data.company.meta.active_process
              ? data.company.meta.active_process
              : null;
        }

        dispatch('process/setName', activeProcessName, { root: true });
        // --
        commit('fetchVacancySuccess', data);
      })
      .catch(err => commit('fetchVacancyFailure', err));
  },

  store({ commit }, model) {
    commit('store');
    let res = Object.assign(model, { active: true });

    return axios
      .post('/v3/vacancies', res)
      .then(response => {
        const data = response.data;

        commit('fetchSuccess', data);
        commit('set_new_vacancy_id', data.id);
      })
      .catch(err => {
        const error = handleError(err);
        commit('fetchFailure', error);
      });
  },

  save({ commit, dispatch, state }, model) {
    let vacancy = Object.assign({}, model);
    console.log(model);

    if (state.local_landing_url_path) {
      vacancy = Object.assign({}, vacancy, {
        landing_url_path: state.local_landing_url_path
      });
    }

    if (!vacancy.id) {
      throw new Error('Vacancy empty id');
    }

    commit('save');

    return Api.updateOne(vacancy).then(data => {
      dispatch('saveSuccess', data);
      dispatch('vacancies/fetchVacanciesList', null, { root: true });
      dispatch('company_new/get_rates', null, { root: true });
    });
  },

  saveVacancyLogoRequest({ commit }, data) {
    const vacancyId = data.vacancyId;
    const logo = data.logo;

    commit('saveVacancyLogoRequest');

    return Api.saveVacancyLogo(vacancyId, logo)
      .then(data => commit('saveVacancyLogoSuccess', data))
      .catch(err => commit('saveVacancyLogoFailure', err));
  },

  removeVacancyLogoRequest({ commit }, data) {
    const vacancyId = data.vacancyId;

    commit('saveVacancyLogoRequest');

    return Api.removeVacancyLogo(vacancyId)
      .then(data => commit('saveVacancyLogoSuccess', data))
      .catch(err => commit('saveVacancyLogoFailure', err));
  },

  publish({ commit, dispatch }, model) {
    commit('save');

    const data = {
      id: model.id,
      is_published: true
    };

    Api.updateOne(data)
      .then(data => {
        if (!model.is_published && data.is_published) {
          commit(
            'updateRoute',
            {
              name: 'vacancies'
            },
            { root: true }
          );
        }

        dispatch('saveSuccess', data);
        dispatch('company_new/get_rates', null, { root: true });
      })
      .catch(err => dispatch('saveFailure', err));
  },

  unpublish({ commit, dispatch }, model) {
    commit('save');

    const data = {
      id: model.id,
      is_published: false
    };

    Api.updateOne(data)
      .then(data => {
        dispatch('saveSuccess', data);
        dispatch('company_new/get_rates', null, { root: true });
      })
      .catch(err => dispatch('saveFailure', err));
  },

  archive({ commit }, model) {
    commit('save');

    const data = {
      id: model.id,
      is_active: false
    };

    return Api.updateOne(data).then(data => commit('saveSuccess', data));
  },

  // join two commits in one action
  saveSuccess({ commit }, data) {
    commit('saveSuccess', data);
    commit('showModalAlert', {
      type: 'success',
      bodyKey: 'vacancy.preferences.modals.saveSuccess.text'
    });
  },

  // join two commits in one action
  saveFailure({ commit }, err) {
    commit('saveFailure', err);
    commit('showModalAlert', {
      type: 'error',
      bodyKey: 'vacancy.preferences.modals.saveFailure.text'
    });
  },

  restore({ commit, dispatch }, model) {
    commit('save');

    const data = {
      id: model.id,
      is_active: true
    };

    return Api.updateOne(data)
      .then(data => {
        dispatch('saveSuccess', data);
        dispatch('company_new/get_rates', null, { root: true });
      })
      .catch(err => dispatch('saveFailure', err));
  },

  fetchPermissions({ commit }, { id }) {
    commit('fetch');

    return Api.fetchPermissions(id)
      .then(data => commit('fetchPermissionsSuccess', data))
      .catch(err => {
        commit('fetchFailure', err);
      });
  },

  clearPermissions({ commit }) {
    commit('clearPermissions');
  },

  savePermissions({ commit, state }) {
    const { id } = state.vacancy;
    const { permissions, permissionsToDelete } = state;

    return Promise.all([
      permissions && permissions.length > 0
        ? Api.updatePermissions({ id, permissions: permissions })
        : null,
      permissionsToDelete && permissionsToDelete.length > 0
        ? Api.deletePermissions({ id, permissions: permissionsToDelete })
        : null
    ]).then(data => {
      commit('savePermissionsSuccess', {
        updatedPermissions: data[0],
        deletedPermissions: data[1]
      });
    });
  },

  fetchCompetencies({ commit }, { id }) {
    return Api.fetchCompetencies(id).then(data => commit('loadedUpdateCompetencies', data));
  },

  fetchAvailableCompetencies({ commit }, { id }) {
    return Api.fetchAvailableCompetencies(id).then(data =>
      commit('updateAvailableCompetencies', data)
    );
  },

  saveCompetencies({ commit, dispatch, state }) {
    const { id } = state.vacancy;
    const { competencies, competenciesToDelete } = state;

    const competenciesToUpdate = competencies.filter(competence => !competence.isNew);
    const competenciesToCreate = competencies.filter(competence => competence.isNew);

    return Promise.all([
      competenciesToUpdate && competenciesToUpdate.length > 0
        ? Api.updateCompetencies(id, competenciesToUpdate)
        : null,
      competenciesToCreate && competenciesToCreate.length > 0
        ? Api.createCompetencies(id, competenciesToCreate)
        : null,
      competenciesToDelete && competenciesToDelete.length > 0
        ? Api.deleteCompetencies(id, competenciesToDelete)
        : null
    ])
      .then(data => {
        commit('saveCompetenciesSuccess', {
          updatedCompetencies: data[0],
          createdCompetencies: data[1],
          deletedCompetencies: data[2]
        });
      })
      .catch(err => {
        dispatch('global/setError', err, { root: true });
        commit('saveCompetenciesFailure', err);
        throw err;
      });
  },

  copyOne({ commit, dispatch, rootState }, model) {
    commit('copyOne');

    return Api.copyOne(model, rootState.process.activeProcessName)
      .then(data => {
        commit('copyOneSuccess', data);
        dispatch('company_new/get_rates', null, { root: true });
        return data;
      })
      .catch(err => {
        commit('copyOneFailure', err);
        throw err;
      });
  },

  deleteOne({ commit, dispatch, rootState }, model) {
    commit('deleteOne');
    let { id, force } = model;
    let url = '/1.0/vacancies';

    if (rootState.process.activeProcessName) {
      url = '/1.0/spec-private/sportmaster/vacancies';
    }

    return axios
      .delete(url, { data: { vacancies: [{ id: id }] }, params: { force } })
      .then(data => {
        commit('deleteOneSuccess', data);
        dispatch('company_new/get_rates', null, { root: true });
      })
      .catch(err => {
        const error = handleError(err);
        dispatch('global/setError', error, { root: true });
        commit('deleteOneFailure', error);
        throw error;
      });
  },

  clearModalAlertData({ commit }) {
    commit('clearModalAlertData');
  },

  getEmailTemplatesDefault({ commit }) {
    commit('getEmailTemplatesDefault');

    return Api.getTempatesDefault()
      .then(data => {
        commit('getEmailTemplatesDefaultSuccess', data);
      })
      .catch(err => {
        commit('getEmailTemplatesDefaultFailure', err);
        throw new Error(err);
      });
  },

  createTemplateDefault({ commit }, models) {
    return Api.createTemplateDefault(models);
  },

  updateEmailTemplateDefault({ commit }, models) {
    return Api.updateTemplateDefault(models);
  },

  deleteEmailTemplateDefault({ commit }, models) {
    return axios
      .delete(`/1.0/response-status-email-template-defaults`, { data: { templates: [models] } })
      .then(response => parseResponse(response).data)
      .catch(err => handleError(err));
  },

  getEmailTemplatesAll({ commit }, { id }) {
    commit('getEmailTemplatesAll');

    return Api.getEmailTemplatesAll(id)
      .then(data => {
        commit('getEmailTemplatesAllSuccess', data);
      })
      .catch(err => {
        // commit('getEmailTemplatesAllFailure', err)
        throw new Error(err);
      });
  },

  getSurveyScreeningTemplate({ commit }, { id }) {
    commit('getSurveyScreeningTemplate');

    return Api.getSurveyScreeningTemplate(id)
      .then(data => {
        commit('getSurveyScreeningTemplateSuccess', data);
      })
      .catch(err => {
        // commit('getEmailTemplatesAllFailure', err)
        throw new Error(err);
      });
  },

  getTestDropoutTemplate({ commit }, { id }) {
    return Api.getTestDropoutTemplate(id)
      .then(data => {
        commit('getTestDropoutTemplateSuccess', data);
      })
      .catch(err => {
        throw new Error(err);
      });
  },

  getInterviewNotificationTemplate({ commit }, { id }) {
    commit('getInterviewNotificationTemplate');

    return axios
      .get(`/v2/vacancies/${id}/interview/remind-emails`)
      .then(response => {
        commit(
          'getInterviewNotificationTemplateSuccess',
          response && response.data && response.data._embedded
            ? response.data._embedded.remind_templates
            : []
        );
        return response.data;
      })
      .catch(error => {
        return Promise.reject(error);
      });
  },

  setInterviewNotificationTemplate({ commit }, { id, model }) {
    commit('setInterviewNotificationTemplate');

    return axios
      .put(`/v2/interview/remind-emails/${model.id}`, model)
      .then(response => {
        return response.data;
      })
      .catch(error => {
        return Promise.reject(error);
      });
  },

  updateEmailTemplate({ commit }, { id, model }) {
    return Api.updateTemplateCustom(id, model);
  },

  updateEmailSurveyTemplate({ commit }, { id, model }) {
    return Api.updateEmailSurveyTemplate(id, model);
  },

  updateEmailScreeningTemplate({ commit }, { id, model }) {
    Api.updateEmailScreeningTemplate(id, model);
  },

  createTemplateCustom({ commit }, { id, model }) {
    return Api.createTemplateCustom(id, model);
  },

  recoverVacancyTemplate({ commit }, { vacancyId, templateIds }) {
    return Api.recoverVacancyTemplate(vacancyId, templateIds);
  },

  deleteEmailTemplate({ commit }, { id, model }) {
    return Api.deleteTemplateCustom(id, model);
  },

  showModalAboutNotAllowedToSaveVacancy({ commit }, { textKey }) {
    commit('showModalAlert', {
      type: 'error',
      bodyKey: textKey
    });
  },

  validate_url({ commit, getters }) {
    let id = getters['one'].id;
    let url = state.local_landing_url_path;

    return axios
      .post('/1.0/vacancies/validate-landing-url-path', {
        landing_url_path: [
          {
            vacancy_id: id,
            landing_url_path: url
          }
        ]
      })
      .then(r => {
        commit('set_landing_url', r.data.data[0]);
      })
      .catch(err => {
        return Promise.reject(err);
      });
  }
};

const mutations = {
  fetchVacancy(state) {
    state.loadingVacancy = true;
    state.error = null;
  },

  fetchVacancySuccess(state, data) {
    state.vacancy = data;
    state.loadingVacancy = false;
  },

  fetchVacancyFailure(state, err) {
    state.error = err;
    state.loadingVacancy = false;
  },

  fetch(state) {
    state.loading = true;
    state.error = null;
  },

  fetchSuccess(state, data) {
    state.vacancy = data;
    state.loading = false;
  },

  fetchFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  create(state) {
    state.vacancy = {
      id: null,
      name: null
    };
    state.loading = false;
  },

  store(state) {
    state.loading = true;
  },

  update(state, { model, data }) {
    Object.assign(model, data);
    state.lastUpdateTimestamp = new Date();
  },

  update_link(state, val) {
    state.local_landing_url_path = val;
    state.lastUpdateTimestamp = new Date();
  },

  set_landing_url(state, val) {
    state.landing_url = val;
  },

  save(state) {
    state.loading = true;
    state.error = null;
  },

  saveSuccess(state, data) {
    state.vacancy = data;
    state.loading = false;
    state.lastSaveTimestamp = new Date();
  },

  saveFailure(state, err) {
    state.loading = false;
    state.error = err;
  },

  saveVacancyLogoRequest(state) {
    state.error = null;
    state.loading = true;
  },

  saveVacancyLogoSuccess(state, data) {
    state.vacancy = setFullName(data);
    state.loading = false;
  },

  saveVacancyLogoFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  updateRoute(state, data) {
    state.route = data;
  },

  set_new_vacancy_id(state, id) {
    state.new_vacancy_id = id;
  },

  updatePermission(state, data) {
    state.permissions = state.permissions.map(permission =>
      permission.user.email === data.user.email ? data : permission
    );
    state.lastUpdateTimestamp = new Date();
  },

  addPermission(state, data) {
    state.permissions = [...state.permissions, data];
    state.lastUpdateTimestamp = new Date();
  },

  deletePermission(state, data) {
    state.permissions = state.permissions.filter(
      permission => permission.user.email !== data.user.email
    );

    state.permissionsToDelete = [...state.permissionsToDelete, data];
    state.lastUpdateTimestamp = new Date();
  },

  clearPermissions(state) {
    state.permissions = [];

    state.lastUpdateTimestamp = new Date();
  },

  fetchPermissionsSuccess(state, permissions) {
    state.permissions = permissions;
    state.loading = false;
  },

  savePermissionsSuccess(state, { updatedPermissions, deletedPermissions }) {
    if (updatedPermissions) {
      state.permissions = state.permissions.map(oldPermission => {
        const newPermission = updatedPermissions.find(
          p => p.user.email === oldPermission.user.email
        );

        return newPermission || oldPermission;
      });
    }

    if (deletedPermissions) {
      const deletedPermissionsEmails = deletedPermissions.map(p => p.user.email);

      state.permissions = state.permissions.filter(
        p => !deletedPermissionsEmails.includes(p.user.email)
      );
      state.permissionsToDelete = state.permissionsToDelete.filter(
        p => !deletedPermissionsEmails.includes(p.user.email)
      );
    }

    state.loading = false;
  },

  updateNewPermission(state, email) {
    state.newPermission = email;
  },

  updateAvailableCompetencies(state, data) {
    state.availableCompetencies = data;
  },

  loadedUpdateCompetencies(state, data) {
    state.competencies = data.sort((a, b) => (a.sort_order > b.sort_order ? 1 : -1));
  },

  updateCompetencies(state, data) {
    state.competencies = data.sort((a, b) => (a.sort_order > b.sort_order ? 1 : -1));
    state.lastUpdateTimestampCompetencies = new Date();
  },

  createCompetence(state, data) {
    const maxSortOrder = Math.max(
      ...state.competencies.map(competence => competence.sort_order).concat(0)
    );

    data = {
      ...data,
      id: new Date().toJSON(),
      local_id: new Date().toJSON(),
      isNew: true,
      sort_order: maxSortOrder + 1
    };

    state.competencies = [...state.competencies, data];
    state.lastUpdateTimestampCompetencies = new Date();
  },

  saveCompetenciesSuccess(
    state,
    { updatedCompetencies, createdCompetencies, deletedCompetencies }
  ) {
    if (updatedCompetencies) {
      const updatedCompetenciesIds = updatedCompetencies.map(c => c.id);
      state.competencies = state.competencies.map(competence => {
        if (updatedCompetenciesIds.includes(competence.id)) {
          return updatedCompetencies.find(c => c.id === competence.id);
        }
        return competence;
      });
    }

    if (createdCompetencies) {
      const createdCompetenciesLocalIds = createdCompetencies.map(c => c.local_id);
      state.competencies = state.competencies.map(competence => {
        if (createdCompetenciesLocalIds.includes(competence.local_id) && competence.isNew) {
          return createdCompetencies.find(c => c.local_id === competence.local_id);
        }

        return competence;
      });
    }

    if (deletedCompetencies) {
      const deletedCompetenciesIds = deletedCompetencies.map(c => c.id);
      state.competencies = state.competencies.filter(
        competence => !deletedCompetenciesIds.includes(competence.id)
      );
    }

    state.loading = false;
    state.lastSaveTimestampCompetencies = new Date();
  },

  saveCompetenciesFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  updateCompetence(state, data) {
    state.competencies = state.competencies.map(competence =>
      competence.id === data.id ? data : competence
    );
    state.lastUpdateTimestampCompetencies = new Date();
  },

  deleteCompetence(state, data) {
    state.competencies = state.competencies.filter(competence => competence.id !== data.id);

    if (!data.isNew) {
      state.competenciesToDelete = [...state.competenciesToDelete, data];
    }

    state.lastUpdateTimestampCompetencies = new Date();
  },

  copyOne(state) {
    state.loading = true;
    state.error = null;
  },

  copyOneSuccess(state, data) {
    state.vacancy = data;
    state.loading = false;
  },

  copyOneFailure(state, err) {
    state.loading = false;
    state.error = err;
  },

  deleteOne(state) {
    state.loading = true;
    state.error = null;
  },

  deleteOneSuccess(state) {
    state.isDeleted = true;
    state.loading = false;
  },

  deleteOneFailure(state, err) {
    state.loading = false;
    state.error = err;
  },

  showModalAlert(state, options) {
    state.modals.alert = {
      show: true,
      options
    };
  },

  clearModalAlertData(state) {
    state.modals.alert = getInitialModalAlertParams();
  },

  competenceIsValid(state, { index, isValid }) {
    state.validCompetences = Object.assign({}, state.validCompetences, { [index]: isValid });
  },

  deleteCompetenceValidation(state, index) {
    delete state.validCompetences[index];
    state.validCompetences = Object.assign({}, state.validCompetences);
  },

  resetChangesTimestamps(state) {
    state.lastSaveTimestamp = null;
    state.lastUpdateTimestamp = null;
    state.lastSaveTimestampCompetencies = null;
    state.lastUpdateTimestampCompetencies = null;
  },

  getEmailTemplatesDefault(state) {
    state.loading = true;
    state.error = null;
  },

  getEmailTemplatesDefaultSuccess(state, data) {
    state.emailTemplatesDefault = data;
    state.loading = false;
  },

  getEmailTemplatesDefaultFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  getEmailTemplatesAll(state) {
    state.loading = true;
    state.error = null;
  },

  getSurveyScreeningTemplate(state) {
    state.loading = true;
    state.error = null;
  },

  getInterviewNotificationTemplate(state) {
    state.loading = true;
    state.error = null;
  },

  setInterviewNotificationTemplate(state) {
    state.loading = true;
    state.error = null;
  },

  getEmailTemplatesAllSuccess(state, data) {
    state.emailTemplatesAll = data;
    state.loading = false;
  },

  getSurveyScreeningTemplateSuccess(state, data) {
    state.surveyScreeningTemplate = data;
    state.loading = false;
  },

  getTestDropoutTemplateSuccess(state, data) {
    state.testDropoutTemplate = data;
    state.loading = false;
  },

  getInterviewNotificationTemplateSuccess(state, data) {
    state.notificationTemplate = data;
    state.loading = false;
  },

  getEmailTemplatesAllFailure(state, err) {
    state.error = err;
    state.loading = false;
  },

  setChange(state, data) {
    state.change = data;
  },

  clear(state) {
    state.vacancy = null;
    state.local_landing_url_path = null;
  }
};

const getters = {
  error(state) {
    return state.error;
  },

  loading(state) {
    return state.loading;
  },

  new_vacancy_id(state) {
    return state.new_vacancy_id;
  },

  one(state) {
    return state.vacancy || { name: 'New vacancy', modules: [] };
  },

  landing_url(state) {
    return state.landing_url;
  },

  local_landing_url_path(state) {
    return state.local_landing_url_path;
  },

  modules(state) {
    return state.vacancy ? state.vacancy.modules : [];
  },

  route(state) {
    return state.route;
  },

  competencies(state) {
    return state.competencies;
  },

  emailTemplatesDefault(state) {
    return state.emailTemplatesDefault;
  },

  emailTemplatesAll(state) {
    return state.emailTemplatesAll;
  },

  surveyScreeningTemplate(state) {
    return state.surveyScreeningTemplate;
  },

  testDropoutTemplate(state) {
    return state.testDropoutTemplate;
  },

  notificationTemplate(state) {
    return state.notificationTemplate;
  },

  availableCompetencies(state) {
    return state.availableCompetencies;
  },

  permissions(state) {
    return state.permissions;
  },

  newPermission(state) {
    return state.newPermission;
  },

  isValid(state) {
    return Object.values(state.validCompetences).filter(v => !v).length === 0;
  },

  haveUnsavedChanges(state) {
    return state.lastUpdateTimestamp > state.lastSaveTimestamp;
  },

  haveUnsavedChangesCompetencies(state) {
    return state.lastUpdateTimestampCompetencies > state.lastSaveTimestampCompetencies;
  },

  change(state) {
    return state.change;
  }
};

export default {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
  modules: {
    searchbot,
    voicebot,
    videointerview,
    invitation,
    sms,
    url,
    test,
    // publicRegistration,
    preferences,
    doNotAllowToSave,
    application
  }
};
